import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Combobox,
  Group,
  Input,
  Loader,
  Menu,
  Stack,
  TextInput,
  Title,
  useCombobox,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCopy,
  IconDots,
  IconId,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import { driverFullCopyText } from "@/lib/driverCopy";
import type {
  Carrier,
  CarriersListResponse,
  Driver,
  DriverCreateRequest,
  DriverUpdateRequest,
  DriversListResponse,
} from "@/types/api";
import { DriverForm, DriverFormApi } from "./DriverForm";

function formatDate(s?: string | null): string {
  if (!s) return "—";
  const d = dayjs(s);
  return d.isValid() ? d.format("DD.MM.YYYY") : s;
}

export default function DriversPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [carrierFilter, setCarrierFilter] = useState<string | null>(null);
  const [debounced] = useDebouncedValue(search, 300);

  // Carrier autocomplete state
  const [carrierSearch, setCarrierSearch] = useState("");
  const [carrierSearchDebounced] = useDebouncedValue(carrierSearch, 300);
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });

  const filters = { page, page_size: 50, q: debounced, carrier_id: carrierFilter ?? undefined };

  const { data, isLoading } = useQuery({
    queryKey: qk.drivers(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.q) params.set("q", filters.q);
      if (filters.carrier_id) params.set("carrier_id", filters.carrier_id);
      return (await api.get<DriversListResponse>(`/api/v1/drivers?${params.toString()}`)).data;
    },
  });

  // Load carriers for autocomplete filter with debounced search
  const { data: carriersData, isFetching: carriersLoading } = useQuery({
    queryKey: qk.carriers({ page: 1, page_size: 50, q: carrierSearchDebounced }),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("page_size", "50");
      if (carrierSearchDebounced) params.set("q", carrierSearchDebounced);
      return (await api.get<CarriersListResponse>(`/api/v1/carriers?${params.toString()}`)).data;
    },
  });
  const carrierItems = carriersData?.items ?? [];

  const [editing, setEditing] = useState<Driver | null | undefined>(undefined);

  // Поддержка ?new=1&carrier_id=X из карточки перевозчика (#93).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const carrierID = searchParams.get("carrier_id") ?? "";
      setEditing({ carrier_id: carrierID } as Driver);
      searchParams.delete("new");
      searchParams.delete("carrier_id");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // formApi хранится в ref, а не в state — DriverForm вызывает onChange(form)
  // на КАЖДОМ рендере (Mantine useForm возвращает новый объект формы каждый
  // раз), и обновление ref не вызывает повторный рендер. Если бы это был
  // useState — был бы бесконечный цикл, А ПРИ [] deps в useEffect — наоборот
  // формы оставались бы со старыми пустыми values.
  const formApiRef = useRef<DriverFormApi | null>(null);
  const setFormApi = useCallback((api: DriverFormApi) => {
    formApiRef.current = api;
  }, []);
  const [confirmDelete, setConfirmDelete] = useState<Driver | null>(null);

  // Для CREATE — пустые поля как undefined (не отправляются).
  const buildCreateBody = (formApi: DriverFormApi): DriverCreateRequest => {
    const v = formApi.values;
    const dateStr = (d: Date | null): string | undefined =>
      d ? dayjs(d).format("YYYY-MM-DD") : undefined;
    return {
      full_name: v.full_name.trim(),
      phone: v.phone.trim() || undefined,
      mobile: v.mobile.trim() || undefined,
      inn: v.inn.trim() || undefined,
      citizenship: v.citizenship || undefined,
      license_category: v.license_category || undefined,
      license_number: v.license_number.trim() || undefined,
      license_issued_at: dateStr(v.license_issued_at),
      license_expires_at: dateStr(v.license_expires_at),
      license_issued_by: v.license_issued_by.trim() || undefined,
      passport_series: v.passport_series.trim() || undefined,
      passport_number: v.passport_number.trim() || undefined,
      passport_issued_by: v.passport_issued_by.trim() || undefined,
      passport_issued_at: dateStr(v.passport_issued_at),
      foreign_passport_number: v.foreign_passport_number.trim() || undefined,
      medical_cert_expires_at: dateStr(v.medical_cert_expires_at),
      carrier_id: v.carrier_id || undefined,
      notes: v.notes || undefined,
    };
  };

  // Для UPDATE — пустые строки ОТПРАВЛЯЕМ как "" (а не undefined!), чтобы бэк
  // через NULLIF реально занулял поле. Если использовать undefined, поле просто
  // не попадёт в PATCH и старое значение сохранится — пользователь жмёт «Сохранить»
  // и его правка теряется.
  const buildUpdateBody = (formApi: DriverFormApi): DriverUpdateRequest => {
    const v = formApi.values;
    const dateStr = (d: Date | null): string => (d ? dayjs(d).format("YYYY-MM-DD") : "");
    return {
      full_name: v.full_name.trim(),
      phone: v.phone.trim(),
      mobile: v.mobile.trim(),
      inn: v.inn.trim(),
      citizenship: v.citizenship,
      license_category: v.license_category,
      license_number: v.license_number.trim(),
      license_issued_at: dateStr(v.license_issued_at),
      license_expires_at: dateStr(v.license_expires_at),
      license_issued_by: v.license_issued_by.trim(),
      passport_series: v.passport_series.trim(),
      passport_number: v.passport_number.trim(),
      passport_issued_by: v.passport_issued_by.trim(),
      passport_issued_at: dateStr(v.passport_issued_at),
      foreign_passport_number: v.foreign_passport_number.trim(),
      medical_cert_expires_at: dateStr(v.medical_cert_expires_at),
      carrier_id: v.carrier_id || undefined,
      notes: v.notes,
    };
  };

  const createMut = useMutation({
    mutationFn: async (body: DriverCreateRequest) =>
      (await api.post<Driver>("/api/v1/drivers", body)).data,
    onSuccess: () => {
      notifications.show({ message: "Водитель создан", color: "green" });
      void qc.invalidateQueries({ queryKey: ["drivers"] });
      setEditing(undefined);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: DriverUpdateRequest }) =>
      (await api.patch<Driver>(`/api/v1/drivers/${id}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Водитель обновлён", color: "green" });
      void qc.invalidateQueries({ queryKey: ["drivers"] });
      setEditing(undefined);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/v1/drivers/${id}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Водитель удалён", color: "green" });
      void qc.invalidateQueries({ queryKey: ["drivers"] });
      setConfirmDelete(null);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    const formApi = formApiRef.current;
    if (!formApi) return;
    const v = formApi.values;
    // Двойная защита — гарантируем, что заявка с пустыми обязательными
    // полями НЕ уйдёт на бэк (даже если validate чего-то не догнал).
    const missing: string[] = [];
    if (!v.full_name?.trim()) missing.push("ФИО");
    if (!v.phone?.trim()) missing.push("Телефон");
    if (!v.license_number?.trim()) missing.push("Номер ВУ");
    if (!v.passport_series?.trim()) missing.push("Серия паспорта");
    if (!v.passport_number?.trim()) missing.push("Номер паспорта");
    if (missing.length > 0) {
      formApi.setErrors({
        full_name: !v.full_name?.trim() ? "Обязательно" : undefined,
        phone: !v.phone?.trim() ? "Обязательно" : undefined,
        license_number: !v.license_number?.trim() ? "Обязательно" : undefined,
        passport_series: !v.passport_series?.trim() ? "Обязательно" : undefined,
        passport_number: !v.passport_number?.trim() ? "Обязательно" : undefined,
      });
      notifications.show({
        title: "Заполните обязательные поля",
        message: missing.join(", "),
        color: "red",
      });
      return;
    }
    if (formApi.validate().hasErrors) return;
    if (editing) {
      const body = buildUpdateBody(formApi);
      const updateBody: DriverUpdateRequest = {
        ...body,
        clear_carrier_id: !body.carrier_id && !!editing.carrier_id,
      };
      updateMut.mutate({ id: editing.id, body: updateBody });
    } else {
      createMut.mutate(buildCreateBody(formApi));
    }
  };

  const columns: DataTableColumn<Driver>[] = [
    {
      key: "full_name",
      header: "ФИО",
      render: (d) => d.full_name,
      filter: { type: "text", getValue: (d) => d.full_name, placeholder: "Поиск по ФИО" },
    },
    {
      key: "phone",
      header: "Телефон",
      render: (d) => d.mobile ?? d.phone ?? "—",
      width: 150,
    },
    {
      key: "license",
      header: "Категория / Номер ВУ",
      render: (d) => {
        if (!d.license_category && !d.license_number) return <span style={{ color: "var(--mantine-color-gray-5)" }}>—</span>;
        return (
          <span>
            {d.license_category && (
              <Badge color="blue" variant="light" size="sm" mr={4}>{d.license_category}</Badge>
            )}
            {d.license_number ?? ""}
          </span>
        );
      },
      width: 180,
    },
    {
      key: "license_expires",
      header: "ВУ до",
      render: (d) => formatDate(d.license_expires_at),
      width: 100,
    },
    {
      key: "carrier",
      header: "Перевозчик",
      render: (d) => d.carrier_name || <span style={{ color: "var(--mantine-color-gray-5)" }}>—</span>,
      width: 180,
    },
    {
      key: "inn",
      header: "ИНН",
      render: (d) =>
        d.inn ? (
          <span style={{ fontFamily: "monospace" }}>{d.inn}</span>
        ) : (
          <span style={{ color: "var(--mantine-color-gray-5)" }}>—</span>
        ),
      width: 130,
    },
    {
      key: "citizenship",
      header: "Гражданство",
      render: (d) => d.citizenship ?? "—",
      width: 100,
    },
    {
      key: "actions",
      header: "",
      width: 60,
      render: (d) => (
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconCopy size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(driverFullCopyText(d)).then(
                  () => notifications.show({ message: "Данные водителя скопированы", color: "teal" }),
                  () => notifications.show({ message: "Не удалось скопировать", color: "red" }),
                );
              }}
            >
              Скопировать все данные
            </Menu.Item>
            <Can perm="drivers:write">
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={() => setEditing(d)}
              >
                Редактировать
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => setConfirmDelete(d)}
              >
                Удалить
              </Menu.Item>
            </Can>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Водители</Title>
        <Can perm="drivers:write">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing(null)}>
            Создать
          </Button>
        </Can>
      </Group>

      <Group>
        <TextInput
          placeholder="Поиск по ФИО, телефону, номеру ВУ..."
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ maxWidth: 360 }}
          leftSection={<IconId size={16} />}
        />
        <Combobox
          store={combobox}
          onOptionSubmit={(val) => {
            const carrier = carrierItems.find((c) => c.id === val);
            if (carrier) {
              setCarrierFilter(carrier.id);
              setCarrierSearch(carrier.name);
            }
            setPage(1);
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <Input
              placeholder="Все перевозчики"
              value={carrierSearch}
              onChange={(e) => {
                setCarrierSearch(e.currentTarget.value);
                if (!e.currentTarget.value) {
                  setCarrierFilter(null);
                  setPage(1);
                }
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
              rightSection={carriersLoading ? <Loader size={14} /> : (carrierSearch ? (
                <span
                  style={{ cursor: "pointer", color: "var(--mantine-color-gray-5)", fontSize: 16, lineHeight: 1 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setCarrierSearch("");
                    setCarrierFilter(null);
                    setPage(1);
                  }}
                >×</span>
              ) : null)}
              style={{ minWidth: 220 }}
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              {carrierItems.length === 0 ? (
                <Combobox.Empty>Перевозчики не найдены</Combobox.Empty>
              ) : (
                carrierItems.map((c: Carrier) => (
                  <Combobox.Option key={c.id} value={c.id}>
                    {c.name}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </Group>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(d) => d.id}
        onRowClick={(d) => setEditing(d)}
      />

      <FormModal
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={editing ? "Редактировать водителя" : "Новый водитель"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
        size="xl"
      >
        <DriverForm initial={editing ?? undefined} onChange={setFormApi} />
      </FormModal>

      <ConfirmDialog
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        title="Удалить водителя?"
        message={`Водитель "${confirmDelete?.full_name}" будет удалён. Действие необратимо.`}
        loading={deleteMut.isPending}
      />
    </Stack>
  );
}
